<script lang="ts">
  import {onDestroy, onMount} from 'svelte';
  import type {Video} from '@/types/project';
  import type {User} from '@/types/user';
  import {menu, step, user, isMobile} from '@/store';
  import {canAccess, formatDuration} from '@/utils/common';
  import {Features, MENU, STEP} from '@/types/common';

  import Dialog from '../../../components/dialog/Dialog.svelte';
  import DeleteVideoDialog from '../Dialogs/DeleteVideoDialog.svelte';
  import RenameVideoDialog from '../Dialogs/RenameVideoDialog.svelte';

  import SermonClips from './VideoInformationDialog/SermonClips.svelte';
  import DiscussionGuide from './VideoInformationDialog/DiscussionGuide';
  import Transcription from './VideoInformationDialog/Transcription.svelte';
  import Audio from './VideoInformationDialog/Audio.svelte';
  import Summaries from './VideoInformationDialog/Summaries/Summaries.svelte';
  import Blogs from './VideoInformationDialog/Blogs';
  import UpgradePlan from './VideoInformationDialog/UpgradePlan.svelte';
  import Thumbnails from './VideoInformationDialog/Thumbnails.svelte';
  import Devotionals from './VideoInformationDialog/Devotionals';
  import Quotes from './VideoInformationDialog/Quotes.svelte';
  import SermonRecap from './VideoInformationDialog/SermonRecap.svelte';
  import AudioDownloadIndicator from './VideoInformationDialog/AudioDownloadIndicator.svelte';
  import {appEvents, VideoService} from '../../../services';
  import {events} from '@/utils/custom-event-names';
  import {selVideo, vidDialogOpen} from '@/pages/thumbPreview/viddialog-store';
  import {tooltip, tooltipRight} from '@/actions/tippy-action';
  import appConfig from '@/config';
  import {writable} from 'svelte/store';
  import {initDownloadOptionsContext} from '@/contexts/download';
  import Carousel from './VideoInformationDialog/Carousel/Carousel.svelte';
  import Icon from '@/components/icon/Icon.svelte';
  //import SermonRecap from "./VideoInformationDialog/SermonRecap.svelte";
  export let isVideoDialogOpen = false;
  export let selectedVideo: Video | null;
  let checked = false;
  let currentUser: User;
  const userSubscription = user.subscribe(value => (currentUser = value));
  let isDeleteDialogOpen = false;
  let selectedVideos: Video[] = [];

  initDownloadOptionsContext();
  selVideo.subscribe(value => {
    // if (value) {
    //     value.transcription = [];
    // }
    selectedVideo = value;
    if (selectedVideo && !checked) {
      checkSermonSend();
      checked = true;
    }
  });

  function closeDialog() {
    if ($step === STEP.CHOOSE_VIDEO) {
      menu.set(MENU.CLIPS);
    }
    vidDialogOpen.set(false);
    checked = false;
  }

  function deleteVideo() {
    if (!selectedVideo) return;
    selectedVideos.push(selectedVideo);
    selectedVideos = selectedVideos;
    isDeleteDialogOpen = true;
  }

  function redirect(link: string) {
    window.location.href = link;
  }

  let selectedRenameVideo: Video | null;

  async function renameVideo() {
    if (!selectedVideo) return console.error('selectedVideo is null');
    selectedRenameVideo = selectedVideo;
  }

  function openSermonSend() {
    if (appConfig.sermonSendUrl && selectedVideo?.id)
      redirect(
        `${appConfig.sermonSendUrl}/admin/load/editor-by-video/${selectedVideo?.id}?token=${window.brushApiToken}`
      );
    else console.error('SermonSend URL not set or selectedVideo is null');
  }

  $: if (isVideoDialogOpen) if (!$menu) menu.set(MENU.CLIPS);
  $: transcriptionLoaded = selectedVideo?.transcription !== null;
  $: videoBtnClass = `p-4 py-1 hover:bg-zinc-100 dark:hover:bg-zinc-800 flex items-center gap-2 rounded-lg ${transcriptionLoaded ? '' : 'loadingBtn  cursor-wait bg-clip-text text-transparent bg-gradient-to-r from-gray-100 via-gray-600 to-gray-200 animate-gradient '}`;

  let sermonSendExists = writable(false);

  async function checkSermonSend() {
    // console.log('selVideo',selectedVideo?.id, $selVideo?.id)
    sermonSendExists.set(false);
    const sermonSendApiUrl = appConfig.sermonSendApiUrl;
    if (!sermonSendApiUrl || !selectedVideo?.id) return;
    const response = await fetch(`${sermonSendApiUrl}/api/sermon-summary-by-videoId/${selectedVideo?.id}`, {
      method: 'GET',
      headers: {
        Authorization: 'Bearer ' + window.brushApiToken
      }
    });
    if (response.status === 200) {
      sermonSendExists.set(true);
    }
  }

  onMount(() => {
    appEvents.on('transcription-completed', async data => {
      if (selectedVideo && selectedVideo.id == data.identifier) {
        selectedVideo = await new VideoService().getVideo(data.identifier);
        transcriptionLoaded = true;
        selVideo.set(selectedVideo);
        closeWarningMessage();
      }
    });

    appEvents.on('reprocess-audio', data => {
      if (selectedVideo && selectedVideo.id == data.identifier) {
        transcriptionLoaded = false;
      }
    });

    appEvents.on('reprocess-transcription', data => {
      if (selectedVideo && selectedVideo.id == data.identifier) {
        transcriptionLoaded = false;
      }
    });
  });

  onDestroy(() => userSubscription());

  let warningMessageElement: HTMLElement | null = null;
  function checkForLoadingTranscriptionOnHover(e: any) {
    if (!transcriptionLoaded) {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();

      if (warningMessageElement) {
        warningMessageElement.classList.remove('hidden');
        const targetRect = e.target.getBoundingClientRect();
        const offsetX = targetRect.x + targetRect.width + 30;
        const offsetY = targetRect.y + 10;
        warningMessageElement.style.left = `${offsetX}px`;
        warningMessageElement.style.top = `${offsetY}px`;
      }

      return;
    }
  }

  function closeWarningMessage() {
    if (warningMessageElement) {
      warningMessageElement.classList.add('hidden');
    }
  }

  let menuOpen: boolean = true;
  function openMenu(mn: MENU) {
    step.set(STEP.CHOOSE_VIDEO);
    menuOpen = false;
    menu.set(mn);
  }

  function showMenu() {
    menuOpen = true;
  }
</script>

<Dialog bind:isDialogOpen={$vidDialogOpen} on:close={closeDialog} autoDismissDialog={true}>
  <div class="absolute z-50 hidden" bind:this={warningMessageElement}>
    <svg
      width="6"
      height="13"
      viewBox="0 0 6 13"
      class="absolute -left-[5.5px] top-[20px]"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M0.707107 6.5L5.5 1.70711V11.2929L0.707107 6.5Z" fill="#E4E4E7" stroke="#A1A1AA" />
    </svg>

    <div class="block max-w-[200px] rounded-md border border-gray-400 bg-gray-200 p-2 text-[14px]">
      <b class="mb-2 block text-[#202020]">Currently Generating...</b>
      <p class="block text-[#45454F]">This typically takes less than 5 minutes</p>
    </div>
  </div>
  <div class="pointer-events-none h-full w-full">
    <div
      class={$isMobile
        ? 'flex-start pointer-events-auto flex h-full w-full flex-col bg-white'
        : `${$step === STEP.CHOOSE_VIDEO ? 'animateVideo' : 'miniSidebar'} pointer-events-auto flex  h-full w-full flex-col overflow-hidden rounded-br-2xl rounded-tr-2xl border-r border-gray-200 bg-white shadow-lg dark:border-zinc-600 dark:bg-zinc-700`}
    >
      {#if $isMobile && !menuOpen && ($menu === MENU.CLIPS || $menu === MENU.THUMBNAILS)}
        <div class="w-full bg-gradient-to-r from-primary to-secondary py-2 text-center text-xs text-white">
          To edit, kindly open your project from your desktop.
        </div>
      {/if}
      {#if $step === STEP.CHOOSE_VIDEO}
        <div
          class="flex w-full border-b border-b-gray-200 px-8 py-4 dark:bg-zinc-700 dark:text-zinc-100 {$isMobile
            ? 'h-25'
            : 'h-20'}"
        >
          <div class="flex flex-grow flex-col gap-2">
            {#if $isMobile && !menuOpen}
              <div class="cursor-pointer transition-all">
                <span class="text-md inline-block space-x-1 font-bold">
                  <!-- <i class="fa-solid fa-chevron-left mr-2" aria-hidden="true"/> -->
                  <span
                    on:click={() => {
                      dispatchEvent(new Event(events.openSideNav));
                    }}
                    on:keypress={() => {
                      dispatchEvent(new Event(events.openSideNav));
                    }}
                    class="px-1"
                  >
                    <i class="fa-solid fa-bars text-xl text-zinc-600"></i>
                  </span>
                  <span on:click={showMenu} on:keypress={showMenu}>
                    <span> {$menu.replace(/_/g, ' ')} </span>
                    <i class="fa-solid fa-angles-up-down text-sm text-gray-400"></i>
                  </span>
                </span>
              </div>
            {/if}
            <!-- Height of 8rem -->
            <div class="flex h-full items-center gap-2 py-2">
              {#if !$isMobile}
                <button
                  on:click={() => {
                    window.dispatchEvent(new Event(events.openSideNav));
                  }}
                  class="my-1 flex aspect-square items-center justify-center rounded-md p-1 lg:hover:bg-zinc-300"
                  use:tooltip={'Open sidebar'}
                >
                  <i class="fa-solid fa-bars text-2xl text-zinc-600"></i>
                </button>
                <!-- <hr class="border-t border-gray-200 dark:border-gray-500" /> -->
                <div class="mx-4 inline-block h-[68px] w-[2px] bg-gray-300"></div>
                <button class="shrink-0" on:click={closeDialog}
                  ><img
                    src="https://storage.googleapis.com/sermonshots-homepage/v2/images/minilogo.png"
                    use:tooltip={'Back to home'}
                    alt=""
                    class="h-[50px] w-[50px] rounded-full"
                  /></button
                >
                <i class="fa fa-angle-right mx-4 text-5xl text-gray-200" aria-hidden="true"></i>
              {/if}
              <img class="h-[50px] rounded-lg object-cover" src={selectedVideo?.preview} alt="video-thumbnail" />
              <div class="flex h-full flex-col justify-center px-2">
                <h1 class="flex gap-2 py-1 text-sm font-bold text-dark">
                  <span class="trunc">
                    {selectedVideo?.title && $isMobile && selectedVideo.title.length > 60
                      ? selectedVideo.title.substring(0, 60) + '...'
                      : selectedVideo?.title}
                  </span>
                  <button on:click={() => renameVideo()}><i class="fa-solid fa-pen-to-square"></i></button>
                </h1>
                <p class="text-xs text-gray-500 dark:text-zinc-400">
                  Duration: {formatDuration(selectedVideo?.metadata?.duration || 0)}
                </p>
              </div>
            </div>
          </div>
          <button on:click={closeDialog} class="ml-auto rounded-full px-2 hover:bg-zinc-100">
            <i class="fa-solid fa-xmark p-2 text-lg text-black" aria-hidden="true"></i>
          </button>
        </div>
      {/if}
      <!-- <hr class="border dark:border-zinc-600" /> 
             Height of 2px Removed this: Why? Trust me bro-->
      <!-- Content height is subtracted by the height of its sibling to maintain 100% height coverage -->
      <div class="flex h-full flex-row overflow-y-auto">
        <div
          class="flex h-full w-[280px] flex-col gap-2 overflow-y-auto border-r border-zinc-300 p-4 font-semibold text-gray-500 dark:border-zinc-700 lg:h-full {$isMobile
            ? 'z-50 w-full bg-white pb-12'
            : ''} {!menuOpen && $isMobile ? 'hidden' : ''}"
        >
          <div class="flex w-full flex-row justify-between">
            <div class="mb-4 text-2xl text-dark font-primary font-bold">Repurposed Content</div>
            {#if $isMobile && $step !== STEP.CHOOSE_VIDEO}
              <i class="fa-solid fa-xmark cursor-pointer" on:click={closeDialog} on:keypress={closeDialog} />
            {/if}
          </div>
          <div class="text-sm text-gray-400">Social Media</div>
          <button
            on:click={() => openMenu(MENU.CLIPS)}
            use:tooltipRight={'Reels, TikTok, Shorts'}
            class=" flex w-full items-center gap-2 rounded-lg p-4 py-1 transition-colors ease-in-out hover:bg-zinc-100 dark:hover:bg-zinc-800 {$menu ===
              MENU.CLIPS && !$isMobile
              ? 'bg-gray-100 text-primary dark:bg-zinc-800'
              : 'text-left text-gray-500 hover:text-primary dark:text-zinc-300'}"
          >
            <i class="fa-solid fa-clapperboard" />
            <span class="group flex flex-col items-start">
              <div class="text-bold {$menu === MENU.CLIPS && !$isMobile ? 'text-left dark:bg-zinc-800' : 'text-left '}">
                Sermon Clips
              </div>
              <div
                class="text-sm font-light {$menu === MENU.CLIPS
                  ? ''
                  : 'deschide transition-opacity ease-in-out'} text-left"
              >
                Reels, TikTok, Shorts
              </div>
            </span>
          </button>
          <!-- {#if isAdmin}
                            <button on:click={() => (openMenu(MENU.RECAP))} class="p-4 hover:bg-zinc-100 dark:hover:bg-zinc-800 flex items-center gap-2 rounded-lg {$menu === MENU.RECAP ? 'bg-orange-50 text-primary bg-gray-100 dark:bg-zinc-800' : 'text-left '}">
                                <i class="fa-solid fa-clapperboard" />
                                <span>Sermon Recap</span>
                            </button>
                        {/if} -->
          <button
            on:click={() => transcriptionLoaded && openMenu(MENU.THUMBNAILS)}
            on:mouseover|self={checkForLoadingTranscriptionOnHover}
            on:focus={checkForLoadingTranscriptionOnHover}
            on:mouseleave|self={closeWarningMessage}
            on:blur={closeWarningMessage}
            use:tooltipRight={'Thumbnails and Quotes Images'}
            class=" transition-colors ease-in-out {videoBtnClass} {$menu === MENU.THUMBNAILS && !$isMobile
              ? 'bg-gray-100 text-primary dark:bg-zinc-800  '
              : 'text-left text-gray-500 hover:text-primary dark:text-zinc-300'}"
          >
            <i class="fa-solid fa-image"></i>
            <span class="flex flex-col items-start">
              <div
                class="text-bold {$menu === MENU.THUMBNAILS && !$isMobile
                  ? 'text-left dark:bg-zinc-800'
                  : 'text-left '}"
              >
                Sermon Images
              </div>
              <div
                class="text-left text-sm font-light {$menu === MENU.THUMBNAILS
                  ? ''
                  : 'deschide transition-opacity ease-in-out'}"
              >
                Thumbnails and Quotes Images
              </div>
            </span>
            <i class="fa-solid fa-lock ml-auto {canAccess(currentUser, Features.THUMBNAIL) ? 'hidden' : ''}" />
          </button>
          <button
            on:click={() => transcriptionLoaded && openMenu(MENU.QUOTES)}
            on:mouseover|self={checkForLoadingTranscriptionOnHover}
            on:focus={checkForLoadingTranscriptionOnHover}
            on:mouseleave|self={closeWarningMessage}
            on:blur={closeWarningMessage}
            use:tooltipRight={'more social media content'}
            class=" transition-colors ease-in-out {videoBtnClass} {$menu === MENU.QUOTES && !$isMobile
              ? 'bg-gray-100  text-primary dark:bg-zinc-800  '
              : 'text-left text-gray-500 hover:text-primary dark:text-zinc-300'}"
          >
            <i class="fa-solid fa-quote-left" />
            <span class="flex flex-col items-start">
              <div
                class="text-bold {$menu === MENU.QUOTES && !$isMobile ? 'text-left dark:bg-zinc-800' : 'text-left '}"
              >
                Quotes & Verses
              </div>
              <div
                class="text-left text-sm font-light {$menu === MENU.QUOTES
                  ? ''
                  : 'deschide transition-opacity ease-in-out'}"
              >
                more social media content
              </div>
            </span>
            <i class="fa-solid fa-lock ml-auto {canAccess(currentUser, Features.SUMMARY) ? 'hidden' : ''}" />
          </button>
          <button
            on:click={() => transcriptionLoaded && openMenu(MENU.CAROUSEL)}
            on:mouseover|self={checkForLoadingTranscriptionOnHover}
            on:focus={checkForLoadingTranscriptionOnHover}
            on:mouseleave|self={closeWarningMessage}
            on:blur={closeWarningMessage}
            use:tooltipRight={'Swipeable stories that stick and engage'}
            class=" transition-colors ease-in-out {videoBtnClass} {$menu === MENU.CAROUSEL && !$isMobile
              ? 'bg-gray-100  text-primary dark:bg-zinc-800  '
              : 'text-left text-gray-500 hover:text-primary dark:text-zinc-300'}"
          >
            <!-- <i class="fa-solid fa-quote-left" /> -->
            <Icon name="Carousel" className="text-2xl" />
            <span class="flex flex-col items-start">
              <div
                class="text-bold {$menu === MENU.CAROUSEL && !$isMobile ? 'text-left dark:bg-zinc-800' : 'text-left '}"
              >
                Social Carousel
              </div>
              <div
                class="text-left text-sm font-light {$menu === MENU.CAROUSEL
                  ? ''
                  : 'deschide transition-opacity ease-in-out'}"
              >
                Swipeable stories that stick and engage
              </div>
            </span>
            <i class="fa-solid fa-lock ml-auto {canAccess(currentUser, Features.SUMMARY) ? 'hidden' : ''}" />
          </button>
          <div class="mt-4 text-sm text-gray-400">Discipleship Content</div>
          <button
            on:click={() => transcriptionLoaded && openMenu(MENU.DISCUSSION_GUIDE)}
            on:mouseover|self={checkForLoadingTranscriptionOnHover}
            on:focus={checkForLoadingTranscriptionOnHover}
            on:mouseleave|self={closeWarningMessage}
            on:blur={closeWarningMessage}
            use:tooltipRight={'For small groups'}
            class=" transition-colors ease-in-out {videoBtnClass} {$menu === MENU.DISCUSSION_GUIDE && !$isMobile
              ? 'bg-gray-100  text-primary dark:bg-zinc-800'
              : 'text-left text-gray-500 hover:text-primary dark:text-zinc-300'}"
          >
            <i class="fa-regular fa-clipboard" />
            <span class="flex flex-col items-start">
              <div
                class="text-bold {$menu === MENU.DISCUSSION_GUIDE && !$isMobile
                  ? 'text-left dark:bg-zinc-800'
                  : 'text-left '}"
              >
                Discussion Guide
              </div>
              <div
                class="text-left text-sm font-light {$menu === MENU.DISCUSSION_GUIDE
                  ? ''
                  : 'deschide transition-opacity ease-in-out'}"
              >
                For small groups
              </div>
            </span>
            <i class="fa-solid fa-lock ml-auto {canAccess(currentUser, Features.DISCUSSION_GUIDE) ? 'hidden' : ''}" />
          </button>
          <button
            on:click={() => transcriptionLoaded && openMenu(MENU.DEVOTIONALS)}
            on:mouseover|self={checkForLoadingTranscriptionOnHover}
            on:focus={checkForLoadingTranscriptionOnHover}
            on:mouseleave|self={closeWarningMessage}
            on:blur={closeWarningMessage}
            use:tooltipRight={"Daily Reflections from Sunday's Message"}
            class=" transition-colors ease-in-out {videoBtnClass} {$menu === MENU.DEVOTIONALS && !$isMobile
              ? 'bg-gray-100 text-primary dark:bg-zinc-800  '
              : 'text-left text-gray-500 hover:text-primary dark:text-zinc-300'}"
          >
            <i class="fa-solid fa-bible" />
            <span class="flex flex-col items-start">
              <div
                class="text-bold {$menu === MENU.DEVOTIONALS && !$isMobile
                  ? 'text-left dark:bg-zinc-800'
                  : 'text-left '}"
              >
                5-Day Devotional
              </div>
              <div class="text-left text-sm font-light {$menu === MENU.DEVOTIONALS ? '' : 'deschide'}">
                Daily Reflections from Sunday's Message
              </div>
            </span>
            <i class="fa-solid fa-lock ml-auto {canAccess(currentUser, Features.SUMMARY) ? 'hidden' : ''}" />
          </button>

          <div class="mt-4 text-sm text-gray-400">More Content</div>
          <button
            on:click={() => transcriptionLoaded && openMenu(MENU.TRANSCRIPTION)}
            on:mouseover|self={checkForLoadingTranscriptionOnHover}
            on:focus={checkForLoadingTranscriptionOnHover}
            on:mouseleave|self={closeWarningMessage}
            on:blur={closeWarningMessage}
            use:tooltipRight={'Of the full sermon'}
            class=" transition-colors ease-in-out {videoBtnClass} {$menu === MENU.TRANSCRIPTION && !$isMobile
              ? 'bg-gray-100 text-primary dark:bg-zinc-800'
              : 'text-left text-gray-500 hover:text-primary dark:text-zinc-300'}"
          >
            <i class="fa-regular fa-subtitles" />
            <span class="flex flex-col items-start">
              <div
                class="text-bold {$menu === MENU.TRANSCRIPTION && !$isMobile
                  ? 'text-left dark:bg-zinc-800'
                  : 'text-left '}"
              >
                Transcription
              </div>
              <div
                class="text-left text-sm font-light {$menu === MENU.TRANSCRIPTION
                  ? ''
                  : 'deschide transition-opacity ease-in-out'}"
              >
                Of the full sermon
              </div>
            </span>
            <i class="fa-solid fa-lock ml-auto {canAccess(currentUser, Features.TRANSCRIPTION) ? 'hidden' : ''}" />
          </button>
          <button
            on:click={() => transcriptionLoaded && openMenu(MENU.BLOG)}
            on:mouseover|self={checkForLoadingTranscriptionOnHover}
            on:focus={checkForLoadingTranscriptionOnHover}
            on:mouseleave|self={closeWarningMessage}
            on:blur={closeWarningMessage}
            use:tooltipRight={'Content for your website'}
            class=" transition-colors ease-in-out {videoBtnClass} {$menu === MENU.BLOG && !$isMobile
              ? 'bg-gray-100 text-primary dark:bg-zinc-800'
              : 'text-left text-gray-500 hover:text-primary dark:text-zinc-300'}"
          >
            <i class="fa-solid fa-blog" />
            <span class="flex flex-col items-start">
              <div class="text-bold {$menu === MENU.BLOG && !$isMobile ? 'text-left dark:bg-zinc-800' : 'text-left '}">
                Blog
              </div>
              <div
                class="text-left text-sm font-light {$menu === MENU.BLOG
                  ? ''
                  : 'deschide transition-opacity ease-in-out'}"
              >
                Content for your website
              </div>
            </span>
            <i class="fa-solid fa-lock ml-auto {canAccess(currentUser, Features.BLOG) ? 'hidden' : ''}" />
          </button>
          <button
            on:click={() => transcriptionLoaded && openMenu(MENU.AUDIO)}
            on:mouseover|self={checkForLoadingTranscriptionOnHover}
            on:focus={checkForLoadingTranscriptionOnHover}
            on:mouseleave|self={closeWarningMessage}
            on:blur={closeWarningMessage}
            use:tooltipRight={'For podcasts'}
            class=" transition-colors ease-in-out {videoBtnClass} {$menu === MENU.AUDIO && !$isMobile
              ? 'bg-gray-100 text-primary dark:bg-zinc-800  '
              : 'text-left text-gray-500 hover:text-primary dark:text-zinc-300'}"
          >
            <i class="fa-solid fa-headphones" />
            <span class="flex flex-col items-start">
              <div
                class="text-bold {$menu === MENU.AUDIO && !$isMobile
                  ? 'bg-orange-50 text-left dark:bg-zinc-800'
                  : 'text-left '}"
              >
                Audio
              </div>
              <div
                class="text-left text-sm font-light {$menu === MENU.AUDIO
                  ? ''
                  : 'deschide transition-opacity ease-in-out'}"
              >
                For podcasts
              </div>
            </span>
            <i class="fa-solid fa-lock ml-auto {canAccess(currentUser, Features.AUDIO) ? 'hidden' : ''}" />
          </button>
          <button
            on:click={() => transcriptionLoaded && openMenu(MENU.SUMMARIES)}
            on:mouseover|self={checkForLoadingTranscriptionOnHover}
            on:focus={checkForLoadingTranscriptionOnHover}
            on:mouseleave|self={closeWarningMessage}
            on:blur={closeWarningMessage}
            use:tooltipRight={'in different versions'}
            class=" transition-colors ease-in-out {videoBtnClass} {$menu === MENU.SUMMARIES && !$isMobile
              ? 'bg-gray-100 text-primary dark:bg-zinc-800  '
              : 'text-left text-gray-500 hover:text-primary dark:text-zinc-300'}"
          >
            <i class="fa-solid fa-list-ul" />
            <span class="flex flex-col items-start">
              <div
                class="text-bold {$menu === MENU.SUMMARIES && !$isMobile ? 'text-left dark:bg-zinc-800' : 'text-left '}"
              >
                Summaries
              </div>
              <div
                class="text-left text-sm font-light {$menu === MENU.SUMMARIES
                  ? ''
                  : 'deschide transition-opacity ease-in-out'}"
              >
                in different versions
              </div>
            </span>
            <i class="fa-solid fa-lock ml-auto {canAccess(currentUser, Features.SUMMARY) ? 'hidden' : ''}" />
          </button>

          {#if $sermonSendExists}
            <div class="mt-4 text-sm text-gray-400">Pages</div>
            <div
              class="cursor-pointer grayscale hover:grayscale-0"
              on:click={() => openSermonSend()}
              on:keydown={() => openSermonSend()}
              use:tooltipRight={'Shareable Pages'}
            >
              <img
                alt="sermonsend logo"
                style="width:150px;"
                src="https://storage.googleapis.com/sermonshots-homepage/images/sermonsend/S%20SEND%20LOGO%20ORIGINAL.png"
              />
            </div>
          {/if}
          <!-- <span class="siteLabel">Shareable Pages</span> -->
          <!-- {#if isAdmin} -->
          <!-- {/if} -->
          <!-- <button
                            on:click={() => deleteVideo()}
                            class="mt-auto px-4 py-2 flex items-center gap-2 rounded-lg sermonbg text-white transition-all"
                        >
                            <i class="fa-regular fa-trash" />
                            <span>Delete Video</span>
                        </button> -->
        </div>
        {#if $step === STEP.CHOOSE_VIDEO}
          <div class="h-full w-full overflow-y-auto {menuOpen && $isMobile ? 'hidden' : ''}">
            {#if $menu === MENU.CLIPS}
              <SermonClips bind:selectedVideo bind:transcriptionLoaded />
            {:else if $menu === MENU.RECAP}
              <SermonRecap bind:selectedVideo />
            {:else if $menu === MENU.DISCUSSION_GUIDE}
              {#if canAccess(currentUser, Features.DISCUSSION_GUIDE)}
                {#if selectedVideo}
                  <DiscussionGuide bind:selectedVideo />
                {/if}
              {:else}
                <UpgradePlan planName="Silver" />
              {/if}
            {:else if $menu === MENU.TRANSCRIPTION}
              {#if canAccess(currentUser, Features.TRANSCRIPTION)}
                <Transcription bind:selectedVideo />
              {:else}
                <UpgradePlan planName="Plus" />
              {/if}
            {:else if $menu === MENU.BLOG}
              {#if canAccess(currentUser, Features.BLOG)}
                <Blogs bind:selectedVideo />
              {:else}
                <UpgradePlan planName="Silver" />
              {/if}
            {:else if $menu === MENU.AUDIO}
              {#if canAccess(currentUser, Features.AUDIO)}
                <Audio bind:selectedVideo />
              {:else}
                <UpgradePlan planName="Plus" />
              {/if}
            {:else if $menu === MENU.SUMMARIES}
              {#if canAccess(currentUser, Features.SUMMARY)}
                <Summaries bind:selectedVideo />
              {:else}
                <UpgradePlan planName="Silver" />
              {/if}
            {:else if $menu === MENU.THUMBNAILS}
              {#if canAccess(currentUser, Features.SUMMARY)}
                <Thumbnails bind:selectedVideo />
              {:else}
                <UpgradePlan planName="Silver" />
              {/if}
            {:else if $menu === MENU.QUOTES}
              {#if canAccess(currentUser, Features.SUMMARY)}
                <Quotes bind:selectedVideo />
              {:else}
                <UpgradePlan planName="Silver" />
              {/if}
            {:else if $menu === MENU.CAROUSEL}
              {#if canAccess(currentUser, Features.CAROUSEL)}
                <Carousel bind:selectedVideo />
              {:else}
                <UpgradePlan planName="Silver" />
              {/if}
            {:else if $menu === MENU.DEVOTIONALS}
              {#if canAccess(currentUser, Features.SUMMARY)}
                <Devotionals bind:selectedVideo />
              {:else}
                <UpgradePlan planName="Silver" />
              {/if}
            {:else}
              <div class="flex h-full w-full items-center justify-center">
                <h1 class="text-3xl font-extrabold text-zinc-400 dark:text-gray-500">404. Page Not Found!</h1>
              </div>
            {/if}
          </div>
        {/if}
      </div>
    </div>
  </div>
</Dialog>

<DeleteVideoDialog bind:isDeleteDialogOpen bind:selectedVideos on:delete={closeDialog} />

<RenameVideoDialog
  bind:video={selectedRenameVideo}
  on:rename={() => {
    if (selectedVideo) selVideo.set(selectedVideo);
  }}
/>

<AudioDownloadIndicator />

<style>
  .animate-gradient {
    background-size: 300%;
    -webkit-animation: animatedgradient 6s linear infinite;
    -moz-animation: animatedgradient 6s linear infinite;
    animation: animatedgradient 6s linear infinite;
  }

  @keyframes animatedgradient {
    0% {
      background-position: 0% 50%;
    }
    50% {
      background-position: 100% 50%;
    }
    100% {
      background-position: 0% 50%;
    }
  }

  @keyframes ani {
    0% {
      display: none;
      opacity: 0;
    }
    100% {
      display: block;
      min-width: 280px;
      opacity: 1;
    }
  }

  .animateVideo {
    animation: animateVideoSidebar 0.3s ease-in-out forwards;
  }

  @keyframes animateVideoSidebar {
    0% {
      width: 0%;
      opacity: 0;
    }
    100% {
      width: 90%;
      min-width: 280px;
      opacity: 1;
    }
  }

  .miniSidebar {
    animation: animateMiniSidebar 0.3s ease-in-out forwards;
  }

  @media screen and (min-width: 1024px) {
    @keyframes animateMiniSidebar {
      0% {
        width: 0%;
        opacity: 0;
      }
      100% {
        width: 280px;
        max-width: 280px;
        opacity: 1;
      }
    }
  }

  @media screen and (max-width: 1024px) {
    @keyframes animateMiniSidebar {
      0% {
        width: 0%;
        opacity: 0;
      }
      100% {
        width: 100%;
        opacity: 1;
      }
    }
  }

  @media screen and (min-width: 1024px) {
    .deschide {
      display: none;
      opacity: 0; /* Initially hidden and transparent */
      animation: ani;
    }
  }

  .divhover:hover .deschide {
    display: block;
    opacity: 1; /* Fully visible on mouse over */
  }

  @media (max-width: 1024px) {
    .trunc {
      max-width: 680px;
      text-overflow: ellipsis;
      overflow: hidden;
      height: 2rem;
      line-height: 1rem;
      display: block;
      overflow: hidden;
      text-overflow: ellipsis;
      position: relative;
      display: -webkit-box;
      -webkit-box-orient: vertical;
      -webkit-line-clamp: 2;
      line-clamp: 2;
    }
  }

  @media (max-width: 512px) {
    .trunc {
      max-width: 220px;
    }
  }
</style>
